import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { Skeleton } from '@/components/ui/Skeleton'
import { isChunkLoadError, reloadOnceForStaleChunk } from '@/utils/chunkReload'
import { reportError } from '@/utils/reportError'

interface ErrorBoundaryProps {
  readonly children: ReactNode
  /** Shown instead of the default message when provided. */
  readonly fallback?: ReactNode
  /** Identifies where the boundary sits, e.g. 'app' or 'reservations'. */
  readonly scope?: string
  /**
   * When this value changes the boundary clears its error and renders its
   * children again. The shell passes the route path: a crash on one screen
   * must not follow the user to every screen they open next.
   */
  readonly resetKey?: string
}

interface ErrorBoundaryState {
  readonly error: Error | null
  /** A stale screen chunk failed to load and a reload is being attempted. */
  readonly reloadingStaleChunk: boolean
}

/**
 * Catches render-time crashes so a single broken screen never blanks the whole
 * portal. Errors go through `reportError` (dev log, lazy tracker in prod).
 *
 * Data-fetching errors do NOT reach here — React Query surfaces those to the
 * screen, which renders them inline (see DataTable's error state).
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null, reloadingStaleChunk: false }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error, reloadingStaleChunk: isChunkLoadError(error) }
  }

  override componentDidUpdate(previousProps: ErrorBoundaryProps): void {
    if (this.state.error && previousProps.resetKey !== this.props.resetKey) this.reset()
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // A stale chunk is recovered by a reload, not reported; if the reload guard
    // refuses, the chunk is genuinely broken and falls through to the report.
    if (this.state.reloadingStaleChunk) {
      if (reloadOnceForStaleChunk()) return
      this.setState({ reloadingStaleChunk: false })
    }

    reportError(error, {
      scope: this.props.scope ?? 'render',
      extra: { componentStack: info.componentStack },
    })
  }

  private readonly reset = (): void => {
    this.setState({ error: null, reloadingStaleChunk: false })
  }

  override render(): ReactNode {
    const { error, reloadingStaleChunk } = this.state
    if (!error) return this.props.children
    if (reloadingStaleChunk) return <Skeleton variant="form" rows={3} label="Loading" />
    if (this.props.fallback) return this.props.fallback

    return (
      <EmptyState
        title="Something Went Wrong"
        description={
          import.meta.env.DEV ? error.message : 'The screen failed to load. Please try again.'
        }
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={this.reset}>
              Try Again
            </Button>
            <Button onClick={() => window.location.reload()}>Reload</Button>
          </div>
        }
      />
    )
  }
}
